import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
}

/**
 * The right-hand column once the invoice is paid — it replaces the QR and the
 * bank details in place, while the order summary on the left stays put. Showing
 * a small standalone card instead threw away the context of what was bought and
 * read as a different page.
 *
 * `justPaid` marks the case where the status flipped while the teacher was
 * watching. That moment is worth marking, and it reads differently from opening
 * an invoice that was settled days ago.
 */
export function PaymentConfirmation({
  paidAt,
  periodEnd,
  justPaid,
}: {
  paidAt: string | null;
  periodEnd: string | null;
  justPaid: boolean;
}) {
  const { t } = useTranslation('billing');

  return (
    // Matches the sibling cards' shell so the two columns sit level, and centres
    // its own content vertically — the confirmation is shorter than the QR block
    // it replaces, and a top-aligned stub beside a tall order card looks broken.
    <section className="flex min-h-full flex-col items-center justify-center rounded-lg border border-green-200 bg-green-50/60 p-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
        <CheckCircle2 className="h-7 w-7 text-green-600" strokeWidth={1.8} />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-green-900">
        {justPaid ? t('invoice.justPaidTitle') : t('invoice.paidTitle')}
      </h2>

      <dl className="mt-5 w-full space-y-2.5 text-left text-[13px]">
        {paidAt && (
          <div className="flex justify-between gap-4">
            <dt className="text-green-800/70">{t('invoice.paidAt')}</dt>
            <dd className="text-green-900">{fmtDateTime(paidAt)}</dd>
          </div>
        )}
        {periodEnd && (
          <div className="flex justify-between gap-4">
            <dt className="text-green-800/70">{t('invoice.paidPeriod')}</dt>
            <dd className="font-medium text-green-900">
              {new Date(periodEnd).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
            </dd>
          </div>
        )}
      </dl>

      <Link
        to="/settings/billing"
        className="mt-6 text-[13px] font-medium text-brand-600 hover:underline"
      >
        ← {t('invoice.backToBilling')}
      </Link>
    </section>
  );
}
